import styled from 'styled-components';
import * as React from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Section, TitleSection, Breadcrumb, H1, Subtitle2 } from '@shortlyster/ui-kit';
import { Text, SwitchLabelled, Box, Flex } from '@compono/ui';
import { useParams } from 'react-router-dom';
import Link from '../../../components/Link';
import getCustomerSubtitle from '../../../lib/getCustomerSubtitle';
import OrganisationNavBar from './OrganisationNavBar';
import {
  OrganisationQuery,
  organisationQuery,
  UpdateSsoEnabledOrganisationMutation,
  updateSsoEnabledMutation,
} from '../queries';

const Container = styled.div({
  '.capital': {
    textTransform: 'capitalize',
  },
});

const StyledText = styled(Text)<{ marginBottom: number }>`
  margin-bottom: ${p => p.marginBottom}rem;
  font-weight: bold !important;
`;

const OrganisationSsoPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: orgQueryData, loading: orgQueryLoading } = useQuery<OrganisationQuery>(
    organisationQuery,
    {
      variables: { id },
      fetchPolicy: 'cache-and-network',
    }
  );
  const [updateSsoEnabled, { loading: ssoUpdating }] =
    useMutation<UpdateSsoEnabledOrganisationMutation>(updateSsoEnabledMutation);

  if (orgQueryLoading) return <p>Loading...</p>;
  if (!orgQueryData?.organisations?.length) return <p>Organisation not found...</p>;

  const organisation = orgQueryData.organisations[0];
  const isNews = organisation?.metadata?.source === 'newscorp';

  const handleSsoChange = async (checked: boolean) => {
    try {
      await updateSsoEnabled({
        variables: {
          id: organisation.id,
          ssoEnabled: checked,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Container>
      <TitleSection>
        <Breadcrumb>
          <Link to="/organisations">Organisations</Link>
          <Link to="" aria-current={true}>
            {organisation.name}
          </Link>
        </Breadcrumb>
        <H1>{organisation.name}</H1>
        <Subtitle2>{getCustomerSubtitle(orgQueryLoading, isNews)}</Subtitle2>
      </TitleSection>
      <OrganisationNavBar organisationId={id as string} active="sso" />
      <Section>
        <StyledText font="h5" marginBottom={1.5}>
          Single Sign-On (SSO)
        </StyledText>
        <Flex sx={{ alignItems: 'center', marginBottom: 3 }}>
          <Box
            sx={{
              width: ['100%', '50%'],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <StyledText font="md" sx={{ marginBottom: 0 }}>
              Enable SSO for this organisation
            </StyledText>
            <SwitchLabelled
              defaultChecked={organisation.ssoEnabled}
              size="sm"
              onChange={async (event: React.ChangeEvent<HTMLInputElement>) =>
                handleSsoChange(event.target.checked)
              }
              disabled={ssoUpdating}
            />
          </Box>
        </Flex>
      </Section>
    </Container>
  );
};

export default OrganisationSsoPage;
